import { handleError } from '@anchorage/sentry';

// The dashboard's /logout is a bare fetch with no timeout, and a hung request
// would never settle — pinning the client to the page it was just denied on.
export const LOGOUT_TIMEOUT_MS = 5_000;

// Apollo calls its 401 handler once per errored operation, so one denial
// arrives N times; and once the first logout clears the cookie, later 401s
// carry no reason header and would otherwise fall through to a login redirect
// that beats this one.
let redirecting = false;

/** Whether a denied client is already on their way to the Access Denied page. */
export const isRedirectingToAccessDenied = () => redirecting;

/** Test-only: clears the latch so each case starts from an un-denied client. */
export const resetAccessDeniedRedirect = () => {
  redirecting = false;
};

// 400 is the no-cookie case: a QR claim denied before one was ever set.
const isSessionAlreadyGone = (response: Response) => response.status === 400;

const rejectAfter = (ms: number) => {
  let timer: ReturnType<typeof setTimeout>;
  const promise = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Timed out clearing the session after ${ms}ms`)),
      ms,
    );
  });
  // Cancelled on the happy path so a settled race leaves behind neither a
  // pending timer nor a rejection with nothing left to catch it.
  return { promise, cancel: () => clearTimeout(timer) };
};

/**
 * Sends a denied client to the Access Denied page, dropping their session on
 * the way out: keeping it makes the login page's claim_session re-deny and
 * bounce them straight back here. `navigate` always runs, cleared or not.
 *
 * Takes both as arguments because the session API and the route table live in
 * frontoffice packages this one must not depend on.
 */
export const redirectToAccessDenied = async (
  logout: () => Promise<Response>,
  navigate: () => void,
) => {
  if (redirecting) {
    return;
  }
  redirecting = true;

  const deadline = rejectAfter(LOGOUT_TIMEOUT_MS);

  try {
    const response = await Promise.race([logout(), deadline.promise]);
    if (!response.ok && !isSessionAlreadyGone(response)) {
      throw new Error(
        `Unable to clear the session with ${response.status} error ${response.statusText}`,
      );
    }
  } catch (e) {
    // Still the right destination with the cookie intact, so report and go.
    handleError(e as Error);
  } finally {
    deadline.cancel();
  }

  navigate();
};
